/**
 * Duplicate Facility Modal
 * Single responsibility: present duplicate form and delegate onConfirm/onClose to parent.
 */

import React, { useState, useEffect } from 'react';
import { X, Check, Copy, Loader2, Lightbulb } from 'lucide-react';
import { COPY_OPTIONS, DEFAULT_DUPLICATE_NAME_SUFFIX } from './constants/duplicateModalCopy';
import {
  INFO_CARD_WRAPPER,
  INFO_CARD_WRAPPER_P5,
  INFO_CARD_LABEL,
  INFO_CARD_VALUE,
  INFO_CARD_TITLE,
  INFO_CARD_TEXT,
  INFO_CARD_ICON_WRAPPER_SM,
  INFO_CARD_ICON_CLASS_SM,
} from '../standards/constants/infoCardStyles';

/**
 * @typedef {Object} Facility
 * @property {string} id
 * @property {string} name
 * @property {string} address
 */

/**
 * @typedef {Object} DuplicateFacilityModalProps
 * @property {boolean} isOpen
 * @property {() => void} onClose
 * @property {(newName: string) => void} onConfirm
 * @property {Facility} facility
 * @property {number} [locationsCount]
 * @property {boolean} [hasCleaningStandards]
 * @property {boolean} [hasSoilLoadLevels]
 * @property {boolean} [hasWaterChanges]
 */

export const DuplicateFacilityModal = ({
  isOpen,
  onClose,
  onConfirm,
  facility,
  locationsCount = 0,
  hasCleaningStandards = false,
  hasSoilLoadLevels = false,
  hasWaterChanges = false,
}) => {
  const [newName, setNewName] = useState(
    `${facility?.name ?? ''}${DEFAULT_DUPLICATE_NAME_SUFFIX}`,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (facility?.name) {
      setNewName(`${facility.name}${DEFAULT_DUPLICATE_NAME_SUFFIX}`);
    }
  }, [facility?.name]);

  const handleConfirm = async () => {
    const trimmed = newName.trim();
    if (!trimmed || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm(trimmed);
      onClose();
    } catch (e) {
      // Parent shows error toast; keep modal open so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNewName(`${facility?.name ?? ''} (Copy)`);
    onClose();
  };

  if (!isOpen) return null;

  const hasAnyStandards = hasCleaningStandards || hasSoilLoadLevels || hasWaterChanges;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-[rgba(184,182,182,0.5)] z-40" onClick={handleCancel} />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-[10px] px-8 py-8 w-[90vw] max-w-[580px] z-50 shadow-2xl">
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#61b8e7]/10 flex items-center justify-center">
                <Copy className="w-5 h-5 text-[#61b8e7]" />
              </div>
              <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black">
                Duplicate Facility
              </h2>
            </div>

            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F2F4F8] hover:bg-gray-300 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-[#1C1B1F]" />
            </button>
          </div>

          {/* Original Facility Name - shared info card style */}
          <div className={INFO_CARD_WRAPPER}>
            <p className={INFO_CARD_LABEL}>Duplicating from:</p>
            <p className={INFO_CARD_VALUE}>{facility?.name}</p>
          </div>

          {/* New Facility Name Input */}
          <div>
            <label className="block font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700 mb-2">
              New facility name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter name for duplicated facility"
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 font-['Roboto:Regular',sans-serif] text-[15px] focus:border-[#61b8e7] focus:outline-none transition-colors"
              autoFocus
            />
          </div>

          {/* What will be copied - shared info card style */}
          <div className={INFO_CARD_WRAPPER_P5}>
            <p className={`${INFO_CARD_TITLE} mb-3`}>
              The following will be copied:
            </p>

            <div className="space-y-2 ml-7">
              <CopyOptionRow
                text={
                  locationsCount > 0
                    ? `All areas (${locationsCount})`
                    : 'No areas yet'
                }
              />
              {COPY_OPTIONS.map(({ key, label }) => (
                <CopyOptionRow
                  key={key}
                  label={label}
                  configured={
                    key === 'hasCleaningStandards'
                      ? hasCleaningStandards
                      : key === 'hasSoilLoadLevels'
                        ? hasSoilLoadLevels
                        : hasWaterChanges
                  }
                />
              ))}
            </div>

            {/* Helper text */}
            <div className="mt-4 pt-4 border-t border-[#A5D8FF]">
              <p className={`${INFO_CARD_TEXT} flex items-center gap-1.5`}>
                <Lightbulb className="w-4 h-4 shrink-0 text-amber-500" aria-hidden />
                <span><span className="font-['Roboto:Medium',sans-serif]">Tip:</span> You can modify
                any setting after duplication.</span>
              </p>
            </div>

            {/* Optional: currently unused, but kept if you want to show a summary line */}
            {hasAnyStandards && (
              <div className={`mt-3 text-[12px] ml-7 ${INFO_CARD_TEXT}`}>
                Standards detected on the original facility — they’ll be copied where applicable.
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={handleCancel}
              className="border-2 border-gray-300 bg-white text-gray-700 rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              disabled={!newName.trim() || isSubmitting}
              className="flex items-center justify-center gap-2 bg-[#61b8e7] text-white rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-[#4da3d1] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
                  Duplicating...
                </>
              ) : (
                'Duplicate Facility'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/** Presentational: one row in "what will be copied" list. */
function CopyOptionRow({ text, label, configured }) {
  const content = text ?? (
    <>
      {label}{' '}
      {configured ? (
        <span className="text-[#10b981] font-['Roboto:SemiBold',sans-serif] font-semibold">
          (configured)
        </span>
      ) : (
        <span className="text-gray-500">(if configured)</span>
      )}
    </>
  );
  return (
    <div className="flex items-center gap-2">
      <div className={INFO_CARD_ICON_WRAPPER_SM}>
        <Check className={INFO_CARD_ICON_CLASS_SM} strokeWidth={3} aria-hidden />
      </div>
      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#333C4D] leading-relaxed">
        {content}
      </p>
    </div>
  );
}
